import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Calendar, Clock, Users, Sparkles, CheckCircle2, BadgeCheck, ArrowRight, Radio,
  Award, Target, Zap, Star, MessageCircle, Loader2, AlertCircle, PlayCircle,
} from "lucide-react";
import ShareButton from "@/components/ShareButton";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const fontStack = { fontFamily: "'IBM Plex Sans', system-ui, sans-serif" };
const displayStack = { fontFamily: "'Outfit', system-ui, sans-serif" };

const TOPIC_LABELS = {
  "meta-ads": "Meta Ads",
  "seo": "SEO",
  "shopify": "Shopify",
  "wordpress": "WordPress",
  "google-ads": "Google Ads",
  "graphic-design": "Graphic Design",
  "video-editing": "Video Editing",
  "other": "Special",
};

const inp = "w-full bg-[#0a0a0a] border border-white/15 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-[#FF0066] transition";

export default function WebinarDetail() {
  const { slug } = useParams();
  const [web, setWeb] = useState(null);
  const [error, setError] = useState(null);
  const [registration, setRegistration] = useState(null);
  const [countdown, setCountdown] = useState("");

  useEffect(() => {
    axios.get(`${API}/webinars/${slug}`).then(({ data }) => setWeb(data)).catch((e) => setError(e.response?.data?.detail || "Webinar not found"));
  }, [slug]);

  useEffect(() => {
    if (!web) return;
    const tick = () => {
      const diff = new Date(web.scheduled_at).getTime() - Date.now();
      if (diff <= 0) { setCountdown("LIVE NOW"); return; }
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setCountdown(d > 0 ? `${d}d ${h}h ${m}m` : `${h}h ${m}m ${s}s`);
    };
    tick();
    const i = setInterval(tick, 1000);
    return () => clearInterval(i);
  }, [web]);

  if (error) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-6 bg-[#050505]" style={fontStack}>
        <div className="bg-[#121212] border border-white/10 rounded-2xl p-8 max-w-md text-center" data-testid="webinar-error">
          <AlertCircle className="w-10 h-10 text-amber-400 mx-auto" />
          <div className="text-xl font-semibold text-white mt-3" style={displayStack}>Webinar not available</div>
          <div className="text-sm text-white/60 mt-2">{error}</div>
          <Link to="/webinars" className="mt-5 inline-flex items-center gap-2 bg-[#FF0066] hover:bg-[#e6005c] text-white font-medium rounded-full px-5 py-2.5 text-sm transition">See all webinars</Link>
        </div>
      </div>
    );
  }
  if (!web) {
    return <div className="min-h-[40vh] flex items-center justify-center text-white/50 bg-[#050505]" style={fontStack}><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div>;
  }
  if (registration) {
    return <ThankYou web={web} regResult={registration} />;
  }

  // Replay mode — webinar ended + replay_url set + available
  const replayAvailable = web.replay_url && (
    web.status === "ended" ||
    (web.replay_available_from && new Date(web.replay_available_from) <= new Date())
  );
  if (replayAvailable && !registration) {
    return <ReplayView web={web} />;
  }

  const dt = new Date(web.scheduled_at);
  const seatsLeft = (web.max_capacity || 500) - (web.registration_count || 0);
  const fillPct = Math.min(100, Math.round(((web.registration_count || 0) / (web.max_capacity || 500)) * 100));

  return (
    <div className="bg-[#050505] text-white min-h-screen" style={fontStack} data-testid="webinar-detail">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/5">
        <div className="absolute -top-40 -right-40 w-[700px] h-[700px] bg-[#FF0066]/15 rounded-full blur-[160px] pointer-events-none" />
        <div className="absolute top-1/3 -left-32 w-[500px] h-[500px] bg-[#FF0066]/10 rounded-full blur-[140px] pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
          <Link to="/webinars" className="inline-flex items-center gap-1.5 text-sm text-white/50 hover:text-white mb-6 transition" data-testid="back-to-webinars">← Back to webinars</Link>
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#FF0066]/15 border border-[#FF0066]/30 text-[#FF0066] text-[11px] uppercase tracking-wider font-semibold">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FF0066] opacity-70" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-[#FF0066]" />
                  </span>
                  Free Live Webinar
                </span>
                <span className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs text-white/80">{TOPIC_LABELS[web.topic] || "Special"}</span>
                <span className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs text-white/80">{web.language || "Hinglish"}</span>
                <span className="px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs">{web.level || "Beginner-friendly"}</span>
              </div>
              <h1 className="mt-6 text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-[1.1] tracking-tight" style={displayStack}>
                {web.title}
              </h1>
              {web.subtitle && <p className="mt-4 text-base sm:text-lg text-white/70 leading-relaxed max-w-2xl">{web.subtitle}</p>}

              <div className="mt-6 grid sm:grid-cols-3 gap-3">
                <Stat icon={Calendar} label="When" value={dt.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" })} sub={dt.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })} />
                <Stat icon={Clock} label="Duration" value={`${web.duration_minutes} min`} sub="+ Q&A" />
                <Stat icon={Users} label="Seats left" value={`${seatsLeft}`} sub={`of ${web.max_capacity || 500}`} />
              </div>

              <div className="mt-6 inline-block bg-gradient-to-br from-[#FF0066]/15 via-[#1a1a1a] to-[#0a0a0a] border border-[#FF0066]/30 rounded-2xl px-6 py-4">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#FF0066] font-bold">Starts in</div>
                <div className="text-3xl sm:text-4xl font-bold text-white mt-1" style={displayStack}>{countdown || "—"}</div>
              </div>

              {/* Seat fill bar */}
              <div className="mt-6 max-w-md">
                <div className="flex items-center justify-between text-xs text-white/60 mb-1.5">
                  <span><Users className="w-3.5 h-3.5 inline mr-1" /> {web.registration_count || 0} already registered</span>
                  <span className={seatsLeft < 50 ? "text-amber-400 font-semibold" : ""}>{fillPct}% filled</span>
                </div>
                <div className="h-2 bg-white/5 rounded-full overflow-hidden border border-white/5">
                  <div className="h-full bg-gradient-to-r from-[#FF0066] to-[#ff3380]" style={{ width: `${fillPct}%` }} />
                </div>
              </div>

              {web.description && (
                <div className="mt-7 text-sm text-white/70 leading-relaxed max-w-2xl whitespace-pre-line">{web.description}</div>
              )}
            </div>

            <div className="lg:col-span-5 lg:sticky lg:top-24">
              <RegisterForm slug={slug} onSuccess={setRegistration} cta={web.cta_text} />
              <div className="mt-3"><ShareButton slug={slug} type="webinar" title={web.title} /></div>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT YOU'LL LEARN */}
      {web.learnings?.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
          <div className="text-xs uppercase tracking-[0.18em] text-[#FF0066] font-semibold">What you'll learn</div>
          <h2 className="text-2xl sm:text-3xl font-bold mt-2 text-white" style={displayStack}>Walk away with a complete playbook</h2>
          <div className="mt-8 grid sm:grid-cols-2 gap-4">
            {web.learnings.map((l, i) => (
              <div key={i} className="bg-[#121212] border border-white/10 hover:border-[#FF0066]/40 rounded-2xl p-5 flex items-start gap-3 transition">
                <div className="w-9 h-9 rounded-lg bg-[#FF0066]/10 flex items-center justify-center shrink-0">
                  <CheckCircle2 className="w-5 h-5 text-[#FF0066]" />
                </div>
                <div className="text-sm text-white/85 leading-relaxed pt-1.5">{l}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* AGENDA */}
      {web.agenda?.length > 0 && (
        <section className="bg-[#0a0a0a] border-y border-white/5 py-14 md:py-20">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-xs uppercase tracking-[0.18em] text-[#FF0066] font-semibold">Agenda</div>
            <h2 className="text-2xl sm:text-3xl font-bold mt-2 text-white" style={displayStack}>Minute-by-minute breakdown</h2>
            <div className="mt-8 space-y-3">
              {web.agenda.map((a, i) => (
                <div key={i} className="bg-[#121212] border border-white/10 rounded-xl p-4 flex items-start gap-4 hover:border-[#FF0066]/30 transition">
                  <div className="text-xs font-mono font-bold text-[#FF0066] shrink-0 w-20 bg-[#FF0066]/10 rounded-md px-2 py-1 text-center">{a.time || `+${i * 10}m`}</div>
                  <div className="text-sm text-white/90 pt-1.5">{a.title}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* HOST */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
        <div className="grid lg:grid-cols-3 gap-8 items-center">
          <div className="lg:col-span-1">
            <div className="relative">
              <div className="absolute -inset-3 bg-[#FF0066]/20 rounded-3xl blur-2xl" />
              {web.host_image ? (
                <img src={web.host_image} alt={web.host_name} className="relative w-full max-w-xs rounded-2xl object-cover border border-white/10 aspect-[4/5]" />
              ) : (
                <div className="relative w-full max-w-xs aspect-[4/5] rounded-2xl bg-[#121212] border border-white/10 flex items-center justify-center text-6xl font-bold text-[#FF0066]" style={displayStack}>
                  {(web.host_name || "K")[0]}
                </div>
              )}
            </div>
          </div>
          <div className="lg:col-span-2">
            <div className="text-xs uppercase tracking-[0.18em] text-[#FF0066] font-semibold">Meet your host</div>
            <h2 className="text-2xl sm:text-3xl font-bold mt-2 text-white" style={displayStack}>{web.host_name || "Kamlesh Singad"}</h2>
            <p className="text-sm text-white/70 mt-4 leading-relaxed">{web.host_bio || "Founder, CodeWithKamlesh · 8+ years digital marketing & training. 1,000+ students trained across India."}</p>
            <div className="mt-5 flex gap-2 flex-wrap">
              {[
                { icon: Award, t: "Google Ads Certified" },
                { icon: Award, t: "Meta Blueprint" },
                { icon: Target, t: "₹2Cr+ Ad Spend Managed" },
                { icon: Star, t: "1,000+ Students Trained" },
              ].map((c, i) => (
                <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs text-white/80">
                  <c.icon className="w-3.5 h-3.5 text-amber-400" /> {c.t}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WHO IS THIS FOR */}
      {web.target_audience?.length > 0 && (
        <section className="bg-[#0a0a0a] border-y border-white/5 py-14 md:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-xs uppercase tracking-[0.18em] text-[#FF0066] font-semibold">Who is this for</div>
            <h2 className="text-2xl sm:text-3xl font-bold mt-2 text-white" style={displayStack}>Yeh webinar perfect hai agar aap…</h2>
            <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {web.target_audience.map((t, i) => (
                <div key={i} className="bg-[#121212] border border-white/10 rounded-xl p-5 flex items-start gap-3 hover:border-[#FF0066]/30 transition">
                  <div className="w-9 h-9 rounded-lg bg-amber-500/10 flex items-center justify-center shrink-0">
                    <Zap className="w-5 h-5 text-amber-400" />
                  </div>
                  <div className="text-sm text-white/90 pt-1.5">{t}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* BONUSES */}
      {web.bonuses?.length > 0 && (
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
          <div className="bg-gradient-to-br from-[#FF0066]/15 via-[#1a1a1a] to-[#0a0a0a] border border-[#FF0066]/30 rounded-3xl p-8 sm:p-12 relative overflow-hidden">
            <div className="absolute -top-20 -right-20 w-[400px] h-[400px] bg-[#FF0066]/10 rounded-full blur-[120px] pointer-events-none" />
            <div className="relative">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500/15 border border-amber-500/40 text-amber-300 text-[11px] uppercase tracking-wider font-bold">
                🎁 Bonus for live attendees
              </span>
              <h2 className="text-2xl sm:text-3xl font-bold mt-4 text-white" style={displayStack}>Live attend karne par FREE bonuses</h2>
              <ul className="mt-7 grid sm:grid-cols-2 gap-4">
                {web.bonuses.map((b, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-white/90">
                    <BadgeCheck className="w-5 h-5 text-[#FF0066] shrink-0 mt-0.5" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      )}

      {/* FINAL CTA */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center border-t border-white/5">
        <h2 className="text-3xl sm:text-4xl font-extrabold text-white" style={displayStack}>
          <span className="text-[#FF0066]">{seatsLeft}</span> seats reh gayi hain
        </h2>
        <p className="text-sm text-white/60 mt-3 max-w-xl mx-auto">Pehle aao pehle pao basis pe registration close ho jata hai. Apni seat abhi reserve karein — free hai aur baad me recording bhi milegi.</p>
        <button onClick={() => document.querySelector('[data-testid="reg-name"]')?.focus()} className="mt-7 inline-flex items-center gap-2 bg-[#FF0066] hover:bg-[#e6005c] text-white font-semibold rounded-full px-7 py-3.5 transition shadow-[0_12px_40px_-8px_rgba(255,0,102,0.6)]">
          <Sparkles className="w-4 h-4" /> {web.cta_text || "Reserve my seat — FREE"} <ArrowRight className="w-4 h-4" />
        </button>
      </section>
    </div>
  );
}

function Stat({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-[#121212] border border-white/10 rounded-xl p-4">
      <Icon className="w-4 h-4 text-[#FF0066]" />
      <div className="text-[10px] uppercase tracking-wider text-white/50 mt-2">{label}</div>
      <div className="text-lg font-bold text-white mt-0.5" style={displayStack}>{value}</div>
      {sub && <div className="text-xs text-white/50">{sub}</div>}
    </div>
  );
}

function RegisterForm({ slug, onSuccess, cta }) {
  const [f, setF] = useState({ name: "", email: "", phone: "", city: "", profession: "", interest: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/webinars/${slug}/register`, f);
      toast.success("You're in! Check your email for joining link.");
      onSuccess(data);
    } catch (er) {
      toast.error(er.response?.data?.detail || "Registration failed");
    } finally { setSubmitting(false); }
  };

  return (
    <form id="register" onSubmit={submit} className="bg-[#121212] border border-white/10 rounded-2xl p-6 space-y-3 shadow-2xl shadow-[#FF0066]/10" data-testid="webinar-register-form">
      <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-[10px] uppercase tracking-wider font-bold">
        ✓ FREE Registration · No card needed
      </div>
      <h3 className="text-xl font-bold text-white" style={displayStack}>Reserve your seat now</h3>
      <input required placeholder="Full Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inp} data-testid="reg-name" />
      <input required type="email" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={inp} data-testid="reg-email" />
      <input required placeholder="WhatsApp Number (+91...)" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className={inp} data-testid="reg-phone" />
      <div className="grid grid-cols-2 gap-2">
        <input placeholder="City" value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} className={inp} />
        <select value={f.profession} onChange={(e) => setF({ ...f, profession: e.target.value })} className={inp}>
          <option value="">I am a…</option>
          <option>Student</option>
          <option>Working Professional</option>
          <option>Business Owner</option>
          <option>Freelancer</option>
          <option>Job Seeker</option>
        </select>
      </div>
      <textarea rows={2} placeholder="Aapka main goal? (optional)" value={f.interest} onChange={(e) => setF({ ...f, interest: e.target.value })} className={inp} />
      <button disabled={submitting} className="w-full inline-flex items-center justify-center gap-2 bg-[#FF0066] hover:bg-[#e6005c] disabled:opacity-60 text-white font-semibold rounded-full px-4 py-3.5 transition shadow-[0_8px_30px_-8px_rgba(255,0,102,0.6)]" data-testid="reg-submit">
        {submitting ? "Reserving…" : (cta || "Reserve my seat — FREE")} <ArrowRight className="w-4 h-4" />
      </button>
      <div className="text-[10px] text-center text-white/40 leading-relaxed">
        Joining link aur WhatsApp group access email pe milega. Hum spam nahi karte.
      </div>
    </form>
  );
}

function ThankYou({ web, regResult }) {
  const join = regResult.webinar?.zoom_join_url || web.zoom_join_url;
  const wa = regResult.whatsapp_group_url;
  const dt = new Date(web.scheduled_at);
  const [embed, setEmbed] = useState(false);

  if (embed) {
    return <WebinarEmbed web={web} regResult={regResult} onLeave={() => setEmbed(false)} />;
  }

  const canJoin = (() => {
    const t = dt.getTime();
    return Date.now() >= t - 15 * 60 * 1000 && Date.now() <= t + (web.duration_minutes || 90) * 60 * 1000 + 15 * 60 * 1000;
  })();

  return (
    <div className="min-h-[80vh] bg-[#050505] text-white" style={fontStack} data-testid="webinar-thankyou">
      <div className="max-w-2xl mx-auto px-4 py-14">
        <div className="bg-gradient-to-br from-[#FF0066]/15 via-[#1a1a1a] to-[#0a0a0a] border border-[#FF0066]/30 rounded-3xl p-8 sm:p-10 text-center relative overflow-hidden">
          <div className="absolute -top-20 -right-20 w-[400px] h-[400px] bg-[#FF0066]/15 rounded-full blur-[120px] pointer-events-none" />
          <div className="relative">
            <div className="w-16 h-16 rounded-full bg-emerald-500 flex items-center justify-center mx-auto shadow-[0_8px_30px_-8px_rgba(16,185,129,0.6)]">
              <CheckCircle2 className="w-9 h-9 text-white" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-extrabold mt-5 text-white" style={displayStack}>You're registered! 🎉</h1>
            <p className="text-sm text-white/70 mt-3">Aapne <b className="text-white">"{web.title}"</b> ke liye successfully register kar liya hai. Joining details aapke email pe bhi bhej diye hain.</p>

            <div className="mt-7 bg-[#121212] border border-white/10 rounded-2xl p-5 text-left">
              <div className="text-xs uppercase tracking-wider text-[#FF0066] font-semibold">Webinar details</div>
              <div className="mt-2 text-sm text-white"><Calendar className="w-4 h-4 inline mr-1.5 text-[#FF0066]" /> {dt.toLocaleString("en-IN", { dateStyle: "full", timeStyle: "short" })}</div>
              <div className="text-sm text-white/80 mt-1"><Clock className="w-4 h-4 inline mr-1.5 text-[#FF0066]" /> {web.duration_minutes} minutes · {web.language || "Hinglish"}</div>
              {join && (
                <div className="mt-4">
                  {canJoin ? (
                    <button onClick={() => setEmbed(true)} className="w-full inline-flex items-center justify-center gap-2 bg-[#FF0066] hover:bg-[#e6005c] text-white font-semibold rounded-full px-4 py-3 transition shadow-[0_8px_30px_-8px_rgba(255,0,102,0.6)]" data-testid="join-webinar-embed">
                      <PlayCircle className="w-5 h-5" /> Join Webinar Now
                    </button>
                  ) : (
                    <div className="text-xs text-white/50">Join button webinar shuru hone se 15 min pehle activate hoga.</div>
                  )}
                </div>
              )}
            </div>

            {wa && (
              <div className="mt-5 bg-emerald-500/5 border border-emerald-500/30 rounded-2xl p-5 text-left">
                <div className="text-xs uppercase tracking-wider text-emerald-300 font-bold">⚡ IMPORTANT — Last step</div>
                <div className="text-sm text-white/90 mt-2">Join our WhatsApp group — reminders, joining link aur bonuses sab yahi share hote hain.</div>
                <a href={wa} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#25D366] hover:bg-[#128C7E] text-white font-semibold transition shadow-[0_8px_30px_-8px_rgba(37,211,102,0.5)]" data-testid="join-wa-group">
                  <MessageCircle className="w-5 h-5" /> Join WhatsApp Group
                </a>
              </div>
            )}

            <div className="mt-6 text-xs text-white/40">
              Questions? WhatsApp <a href="https://wa.me/919131341638" target="_blank" rel="noreferrer" className="text-emerald-300">+91 91313 41638</a> or email <a href="mailto:info@codewithkamlesh.in" className="text-[#FF0066]">info@codewithkamlesh.in</a>
            </div>
            <Link to="/webinars" className="mt-6 inline-flex items-center gap-1.5 text-sm text-white/60 hover:text-white transition">← See all webinars</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function WebinarEmbed({ web, regResult, onLeave }) {
  const startedRef = React.useRef(false);
  const [status, setStatus] = React.useState("Loading Zoom SDK…");
  const [err, setErr] = React.useState(null);

  React.useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    (async () => {
      try {
        let t = 0;
        while (!window.ZoomMtg && t < 60) { await new Promise((r) => setTimeout(r, 100)); t++; }
        const ZoomMtg = window.ZoomMtg;
        if (!ZoomMtg) throw new Error("Zoom SDK failed to load");
        ZoomMtg.setZoomJSLib("https://source.zoom.us/4.0.0/lib", "/av");
        ZoomMtg.preLoadWasm();
        ZoomMtg.prepareWebSDK();

        const meetingNumber = String(web.zoom_meeting_id || "").replace(/\s+/g, "");
        const { data: sig } = await axios.post(`${API}/zoom/sdk-signature-public`, {
          meetingNumber, registrationId: regResult.registration?.id, slug: web.slug,
        });

        setStatus("Initializing…");
        await new Promise((resolve, reject) => {
          ZoomMtg.init({
            leaveUrl: `${window.location.origin}/webinars`,
            patchJsMedia: true, isSupportAV: true, isSupportChat: true,
            isSupportQA: true, isSupportPolling: true,
            showMeetingHeader: true, screenShare: true,
            success: resolve, error: reject,
          });
        });
        setStatus("Joining webinar…");
        await new Promise((resolve, reject) => {
          ZoomMtg.join({
            signature: sig.signature, sdkKey: sig.sdk_key,
            meetingNumber, passWord: web.zoom_password || "",
            userName: regResult.registration?.name || "Attendee",
            userEmail: regResult.registration?.email || "",
            tk: "", zak: "", success: resolve, error: reject,
          });
        });
        setStatus("");
      } catch (e) {
        console.error(e);
        setErr(e?.errorMessage || e?.reason || e?.message || "Failed");
      }
    })();
  }, [web, regResult]);

  if (err) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-6 bg-[#050505]" style={fontStack}>
        <div className="bg-[#121212] border border-white/10 rounded-2xl p-8 max-w-md text-center">
          <AlertCircle className="w-10 h-10 text-amber-400 mx-auto" />
          <div className="text-xl font-bold mt-3 text-white" style={displayStack}>Embed failed</div>
          <div className="text-sm text-white/60 mt-2 break-words">{err}</div>
          <button onClick={onLeave} className="mt-5 inline-flex items-center gap-2 bg-[#FF0066] hover:bg-[#e6005c] text-white font-medium rounded-full px-5 py-2.5 text-sm transition">Back</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center" style={fontStack} data-testid="webinar-embed">
      <div className="text-center px-6">
        <div className="w-20 h-20 rounded-2xl bg-[#FF0066] flex items-center justify-center mb-5 mx-auto shadow-[0_8px_30px_-8px_rgba(255,0,102,0.6)]"><Radio className="w-10 h-10 text-white" /></div>
        <div className="text-2xl font-bold flex items-center justify-center gap-2 text-white" style={displayStack}><Loader2 className="w-5 h-5 animate-spin" /> {status || "Joining…"}</div>
        <div className="text-xs text-white/50 mt-3">{web.title}</div>
      </div>
    </div>
  );
}


function ReplayView({ web }) {
  const url = web.replay_url || "";
  // Convert YouTube URL to embed
  const ytMatch = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([\w-]+)/);
  const ytEmbed = ytMatch ? `https://www.youtube.com/embed/${ytMatch[1]}` : null;
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  const vimeoEmbed = vimeoMatch ? `https://player.vimeo.com/video/${vimeoMatch[1]}` : null;

  return (
    <div className="bg-[#050505] text-white min-h-screen" style={fontStack} data-testid="webinar-replay">
      <section className="border-b border-white/5 py-6 px-4">
        <div className="max-w-5xl mx-auto">
          <Link to="/webinars" className="inline-flex items-center gap-1.5 text-sm text-white/50 hover:text-white">← All webinars</Link>
          <div className="mt-4 flex items-center gap-3 flex-wrap">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-xs uppercase tracking-wider font-semibold">📼 Replay available</span>
            <span className="text-xs text-white/50">Aired on {new Date(web.scheduled_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</span>
          </div>
          <h1 className="mt-3 text-2xl md:text-4xl font-extrabold" style={displayStack}>{web.title}</h1>
          {web.subtitle && <p className="mt-2 text-white/70 text-sm md:text-base max-w-3xl">{web.subtitle}</p>}
        </div>
      </section>
      <section className="py-6 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="aspect-video w-full bg-black rounded-2xl overflow-hidden border border-white/10">
            {ytEmbed ? (
              <iframe className="w-full h-full" src={ytEmbed} title={web.title} frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen data-testid="replay-iframe-yt" />
            ) : vimeoEmbed ? (
              <iframe className="w-full h-full" src={vimeoEmbed} title={web.title} frameBorder="0" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen data-testid="replay-iframe-vimeo" />
            ) : (
              <video src={url} controls className="w-full h-full" data-testid="replay-video-direct" />
            )}
          </div>
          {web.description && <div className="mt-6 text-sm text-white/70 leading-relaxed whitespace-pre-line max-w-3xl">{web.description}</div>}
          {web.related_course_id && (
            <Link to={`/courses/${web.related_course_id}`} className="mt-6 inline-flex items-center gap-2 bg-[#FF0066] hover:bg-[#e6005c] text-white font-semibold rounded-full px-5 py-3 text-sm transition" data-testid="replay-related-course-cta">
              See related course →
            </Link>
          )}
        </div>
      </section>
    </div>
  );
}
